import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Inbox, Clock, CheckCircle, FileText, LogOut, RefreshCw } from 'lucide-react';

const CounsellorDashboard = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const user = JSON.parse(localStorage.getItem('counsellor_user') || '{}');

  useEffect(() => {
    if (!user.id) { navigate('/counsellor-login'); return; }
    axios.get(`${API_BASE}/counsellor/inbox/${user.id}`)
      .then(res => setReports(res.data.reports || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id, navigate]);

  const logout = () => {
    localStorage.removeItem('counsellor_token');
    localStorage.removeItem('counsellor_user');
    navigate('/counsellor-login');
  };

  const handleReschedule = async (reportId) => {
    if (!rescheduleDate || !rescheduleTime) { return; }
    setRescheduling(true);
    try {
      await axios.post(`${API_BASE}/counsellor/reschedule/${reportId}`, {
        date: rescheduleDate, time: rescheduleTime,
      });
      toast.success('Session rescheduled — candidate notified.');
      setRescheduleId(null);
      setRescheduleDate('');
      setRescheduleTime('');
    } catch (err) {
      toast.error('Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  const pending = reports.filter(r => r.status === 'pending');
  const reviewed = reports.filter(r => r.status === 'reviewed');

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* Header */}
      <div className="bg-white border-b border-[#d2d2d7] px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#1d1d1f]">Counsellor Dashboard</h1>
          <p className="text-sm text-[#86868b]">{user.name} &middot; {user.speciality || 'Career Guidance'}</p>
        </div>
        <Button onClick={logout} variant="ghost" className="text-[#86868b]" data-testid="counsellor-logout">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>

      <div className="max-w-5xl mx-auto p-6 sm:p-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6">
            <div className="text-sm text-[#86868b] mb-1">Pending Review</div>
            <div className="text-3xl font-bold text-[#FF9500]">{pending.length}</div>
          </div>
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6">
            <div className="text-sm text-[#86868b] mb-1">Completed</div>
            <div className="text-3xl font-bold text-green-600">{reviewed.length}</div>
          </div>
        </div>

        {/* Inbox */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6">
          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#0071e3]" /> Candidate Inbox
          </h2>

          {loading ? (
            <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0071e3] mx-auto" /></div>
          ) : reports.length === 0 ? (
            <div className="py-12 text-center text-[#86868b]">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No candidate reports yet.</p>
              <p className="text-sm mt-1">Reports will appear here when apprentices book sessions with you.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map(r => (
                <div key={r.report_id} className="rounded-xl border border-[#d2d2d7] overflow-hidden">
                  <button
                    onClick={() => navigate(`/counsellor/report/${r.report_id}`)}
                    className="w-full flex items-center gap-4 p-4 hover:bg-blue-50 transition-all text-left"
                    data-testid={`inbox-report-${r.report_id}`}
                  >
                    {r.status === 'reviewed' ? (
                      <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                    ) : (
                      <Clock className="w-8 h-8 text-[#FF9500] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-[#1d1d1f]">{r.candidate_name}</h3>
                      <p className="text-xs text-[#86868b]">Ref: {r.candidate_ref} &middot; Received: {new Date(r.created_at).toLocaleDateString('en-IE')}</p>
                    </div>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${r.status === 'reviewed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {r.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                    </span>
                  </button>
                  {/* Reschedule */}
                  <div className="px-4 pb-3 flex items-center gap-2 border-t border-[#f5f5f7]">
                    {rescheduleId === r.report_id ? (
                      <div className="flex items-center gap-2 w-full pt-2">
                        <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="px-2 py-1.5 border border-[#d2d2d7] rounded-lg text-xs flex-1" />
                        <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} className="px-2 py-1.5 border border-[#d2d2d7] rounded-lg text-xs w-24" />
                        <Button onClick={() => handleReschedule(r.report_id)} disabled={rescheduling || !rescheduleDate || !rescheduleTime} className="bg-emerald-600 text-white rounded-full text-xs h-8 px-3" data-testid={`confirm-reschedule-${r.report_id}`}>
                          {rescheduling ? '...' : 'Confirm'}
                        </Button>
                        <Button onClick={() => setRescheduleId(null)} variant="ghost" className="text-xs h-8 px-2 text-[#86868b]">Cancel</Button>
                      </div>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setRescheduleId(r.report_id); }} className="flex items-center gap-1.5 text-xs text-[#86868b] hover:text-[#0071e3] pt-2" data-testid={`reschedule-${r.report_id}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Reschedule Session
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CounsellorDashboard;
